// PDF generator for Cronograma de Obra (per stage OR complete with Gantt)
// Uses html2pdf.js + shared canteiro-safe styles from /lib/pdfStyles.js
import html2pdf from "html2pdf.js";
import QRCode from "qrcode";
import { BRAND } from "@/lib/brand";
import {
  PDF_MARGINS,
  escapeHtml,
  fixLegacyText,
  safeText,
} from "@/lib/pdfStyles";

// Generates a small QR code as a data URL (PNG) for embedding in the header.
// Points to the cronograma detail page via the `?openId=` deep-link.
async function buildCronogramaQR(cronogramaId) {
  if (!cronogramaId || typeof window === "undefined") return "";
  try {
    const origin = window.location.origin;
    const url = `${origin}/operacional/cronogramas?openId=${encodeURIComponent(cronogramaId)}`;
    return await QRCode.toDataURL(url, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 240,
      color: { dark: "#1A237E", light: "#FFFFFF" },
    });
  } catch {
    return "";
  }
}

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s + "T00:00:00").toLocaleDateString("pt-BR");
  } catch {
    return s;
  }
};

const STATUS_LABEL = {
  pendente: "Pendente",
  em_andamento: "Em Andamento",
  concluido: "Concluído",
  atrasado: "Atrasado",
};

const STATUS_BG = {
  pendente: { bg: "#FFEBEE", color: "#B71C1C" },
  em_andamento: { bg: "#E3F2FD", color: "#0D47A1" },
  concluido: { bg: "#E8F5E9", color: "#1B5E20" },
  atrasado: { bg: "#FFF3E0", color: "#E65100" },
};

const TIPO_LABEL = {
  chuva: "Chuva", falta_colaborador: "Falta de Colaborador",
  atraso_material: "Atraso de Material", problema_estrutural: "Problema Estrutural",
  interdicao: "Interdição", greve: "Greve",
  acidente: "Acidente de Trabalho", outro: "Outro",
};

const sanitize = (s) =>
  String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "_").slice(0, 40) || "cronograma";

function statusBadge(status) {
  const cfg = STATUS_BG[status] || STATUS_BG.pendente;
  return `<span style="display:inline-block;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:800;background:${cfg.bg};color:${cfg.color};text-transform:uppercase;letter-spacing:0.4px;font-family:'Arial Black',Arial,sans-serif">${STATUS_LABEL[status] || status}</span>`;
}

function headerHtml(titulo, contrato, cron, dataEmissao, qrDataUrl = "") {
  const logoSrc = BRAND.logoDataUrl || BRAND.logoUrl;
  return `
    <div class="brand-bar"><div class="b1"></div><div class="b2"></div></div>
    <div class="header">
      <div class="header-left">
        <img src="${logoSrc}" alt="OBRASUL-RJ" class="brand-logo" crossorigin="anonymous" />
        <div class="brand-text">
          <p class="brand">${BRAND.name}</p>
          <p class="brand-sub">${BRAND.tagline}</p>
        </div>
      </div>
      <div class="doc-meta">
        <div class="num">${escapeHtml(titulo)}</div>
        <div>Emitido em ${dataEmissao}</div>
      </div>
      ${qrDataUrl ? `
        <div class="header-qr" aria-label="QR Code do cronograma">
          <img src="${qrDataUrl}" alt="QR Code" class="qr-img" />
          <div class="qr-caption">Acesse online</div>
        </div>
      ` : ''}
    </div>
    <div class="contrato-info">
      <div><b>Contrato:</b> ${safeText(contrato?.titulo || contrato?.numero || "—")}</div>
      <div><b>Cliente:</b> ${safeText(contrato?.cliente_nome || "—")}</div>
      <div><b>Endereço da obra:</b> ${safeText(contrato?.endereco_obra || "—")}</div>
      <div><b>Período:</b> ${fmtDate(cron.data_inicio)} → ${fmtDate(cron.data_conclusao_prevista)} <span class="muted">(${cron.duracao_prevista_dias} dias)</span></div>
    </div>
  `;
}

const baseStyles = `
<style>
  /* ==== Print-safe CSS · Regras profissionais para impressão A4 ==== */
  @media print {
    @page {
      size: A4 portrait;
      margin: 15mm 10mm 15mm 10mm; /* topo direita base esquerda */
    }
  }
  * {
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  .pdf-cron-root {
    font-family: Arial, 'Helvetica Neue', Helvetica, sans-serif;
    font-size: 12px; /* Arial 12 base — todo texto respeita este tamanho */
    color: #000000;
    width: 1080px;
    background: #ffffff;
    padding: 0 6px;
    line-height: 1.35;
  }
  .pdf-cron-root p { margin: 0; }
  .pdf-cron-root ul, .pdf-cron-root ol { margin: 0; }
  /* Hierarquia de títulos: sempre maiores que o corpo (12px) */
  .pdf-cron-root h1,
  .pdf-cron-root h2,
  .pdf-cron-root h3 {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  /* Blocos internos (título, linhas de tabela, cards de indicadores) — mantêm integridade */
  .pdf-cron-root .card,
  .pdf-cron-root .contrato-info,
  .pdf-cron-root .indicadores,
  .pdf-cron-root tr,
  .pdf-cron-root .etapa-block .etapa-header,
  .pdf-cron-root .etapa-block .titulo-row,
  .pdf-cron-root .etapa-block .meta,
  .pdf-cron-root .plano-block,
  .pdf-cron-root .imprev-row {
    break-inside: avoid;
    page-break-inside: avoid;
  }
  /* Etapas: bloco fluido — pode quebrar entre sub-blocos internos (título+meta / escopo / tabela).
     Cada sub-bloco individual está protegido acima. */
  .pdf-cron-root .etapa-block {
    display: block;
    orphans: 3; widows: 3;
  }
  /* ==== Fim das regras print-safe ==== */

  .pdf-cron-root .brand-bar { display: flex; height: 8px; margin-bottom: 18px; }
  .pdf-cron-root .brand-bar .b1 { flex: 3; background: #1A237E; }
  .pdf-cron-root .brand-bar .b2 { flex: 1; background: #D4A437; }
  .pdf-cron-root .header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 0 4px 14px; border-bottom: 2px solid #1A237E; margin-bottom: 18px;
  }
  .pdf-cron-root .header-left { display: flex; align-items: center; gap: 14px; }
  .pdf-cron-root .brand-logo {
    width: 60px; height: 60px; object-fit: contain; border-radius: 6px;
    background: #1A237E;
  }
  .pdf-cron-root .brand-text p { margin: 0; }
  .pdf-cron-root .brand { font-size: 24px; font-weight: 900; color: #1A237E; letter-spacing: 0.2px; font-family: 'Arial Black', Arial, sans-serif; }
  .pdf-cron-root .brand-sub { font-size: 12px; color: #424242; margin-top: 3px; font-weight: 600; }
  .pdf-cron-root .doc-meta { text-align: right; font-size: 11px; color: #424242; }
  .pdf-cron-root .doc-meta .num { font-size: 14px; color: #1A237E; font-weight: 800; }
  .pdf-cron-root .header-qr {
    display: flex; flex-direction: column; align-items: center; gap: 4px;
    margin-left: 14px; padding-left: 14px; border-left: 1.5px solid #E0E0E0;
  }
  .pdf-cron-root .header-qr .qr-img {
    width: 84px; height: 84px; display: block;
    border: 1.5px solid #1A237E; border-radius: 4px; background: #fff; padding: 2px;
  }
  .pdf-cron-root .header-qr .qr-caption {
    font-size: 10px; font-weight: 700; color: #1A237E; text-transform: uppercase;
    letter-spacing: 0.3px; font-family: Arial, sans-serif;
  }

  .pdf-cron-root h1 { font-size: 24px; font-weight: 900; color: #1A237E; margin: 8px 4px 4px; font-family: 'Arial Black', Arial, sans-serif; }
  .pdf-cron-root h2 { font-size: 16px; font-weight: 800; color: #1A237E; margin: 12px 4px 4px; padding-bottom: 6px; border-bottom: 2px solid #D4A437; font-family: 'Arial Black', Arial, sans-serif; }
  .pdf-cron-root h3 { font-size: 14px; font-weight: 700; color: #1A237E; margin: 8px 4px 4px; font-family: 'Arial Black', Arial, sans-serif; }
  .pdf-cron-root h2 + .etapa-block,
  .pdf-cron-root h2 + .imprev-row,
  .pdf-cron-root h2 + .plano-block,
  .pdf-cron-root h2 + .indicadores,
  .pdf-cron-root h2 + .gantt-wrap { margin-top: 0; }
  .pdf-cron-root .muted { color: #616161; font-weight: 500; }

  .pdf-cron-root .contrato-info {
    background: #F5F5F5; border: 1px solid #BDBDBD; border-radius: 6px;
    padding: 12px 14px; margin: 0 4px 18px; font-size: 12px; color: #212121;
    display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px;
    line-height: 1.4; text-align: justify;
  }
  .pdf-cron-root .contrato-info b { color: #1A237E; }

  .pdf-cron-root .indicadores {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin: 0 4px 12px;
  }
  .pdf-cron-root .indicadores .card {
    border: 1.5px solid #BDBDBD; border-radius: 6px; padding: 8px 12px; background: #ffffff;
  }
  .pdf-cron-root .indicadores .card .lbl { font-size: 11px; color: #424242; text-transform: uppercase; letter-spacing: 0.4px; font-weight: 700; }
  .pdf-cron-root .indicadores .card .val { font-size: 20px; font-weight: 900; color: #1A237E; margin-top: 2px; font-family: 'Arial Black', Arial, sans-serif; }
  .pdf-cron-root .indicadores .card.danger { background: #FFEBEE; border-color: #EF9A9A; }
  .pdf-cron-root .indicadores .card.danger .val { color: #D32F2F; }
  .pdf-cron-root .indicadores .card.ok { background: #E8F5E9; border-color: #A5D6A7; }
  .pdf-cron-root .indicadores .card.ok .val { color: #2E7D32; }

  .pdf-cron-root .etapa-block {
    border: 1.5px solid #BDBDBD; border-radius: 6px; padding: 10px 14px; margin: 0 4px 10px;
    background: #fff;
    font-size: 17px; color: #000000; text-align: justify; line-height: 1.35;
  }
  .pdf-cron-root .etapa-block.critical { border-left: 6px solid #9C27B0; background: #F3E5F5; }
  .pdf-cron-root .etapa-block .titulo-row {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    border-bottom: 1px solid #E0E0E0; padding-bottom: 8px; margin-bottom: 10px;
    text-align: left;
  }
  .pdf-cron-root .etapa-block .titulo-row .ordem {
    font-family: Consolas, 'Courier New', ui-monospace, monospace; font-size: 16px; color: #616161; font-weight: 700;
  }
  .pdf-cron-root .etapa-block .titulo-row .nome { font-size: 20px; font-weight: 800; color: #1A237E; font-family: 'Arial Black', Arial, sans-serif; }
  .pdf-cron-root .etapa-block .titulo-row .duracao { margin-left: auto; font-size: 16px; color: #212121; font-weight: 800; font-family: Consolas, monospace; }
  .pdf-cron-root .etapa-block table.meta {
    width: 100%; border-collapse: collapse; font-size: 17px; color: #000000;
    line-height: 1.4; text-align: left;
  }
  .pdf-cron-root .etapa-block table.meta td {
    padding: 3px 0; vertical-align: top; white-space: normal;
    word-wrap: break-word; overflow-wrap: break-word;
  }
  .pdf-cron-root .etapa-block .meta b { color: #1A237E; font-weight: 800; }
  .pdf-cron-root .etapa-block .escopo {
    font-size: 17px; color: #212121; padding: 10px 12px 6px; margin: 0;
    line-height: 1.45; text-align: justify;
  }
  /* Banner do escopo como div simples — evita bug de thead sendo tratado como bloco atômico */
  .pdf-cron-root .etapa-block .escopo-banner {
    background: #FFF3E0; color: #E65100; font-size: 13px; padding: 5px 12px;
    font-family: 'Arial Black', Arial, sans-serif; font-weight: 800;
    text-align: left; letter-spacing: 0.3px; border-bottom: 1.5px solid #FFB74D;
    border-top: 1px dashed #BDBDBD; margin-top: 10px;
  }
  .pdf-cron-root .etapa-block .escopo b { color: #1A237E; }
  .pdf-cron-root table.itens {
    width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 10px;
    line-height: 1.35;
  }
  .pdf-cron-root table.itens thead { display: table-header-group; }
  .pdf-cron-root table.itens thead th {
    background: #1A237E; color: #fff; padding: 8px 10px; text-align: left; font-weight: 800; font-size: 15px;
    font-family: 'Arial Black', Arial, sans-serif;
  }
  .pdf-cron-root table.itens thead th.continuacao {
    background: #FFF3E0; color: #E65100; font-size: 14px; padding: 5px 10px;
    font-family: Arial, sans-serif; font-weight: 700; text-align: left; letter-spacing: 0.3px;
  }
  .pdf-cron-root table.itens tbody td {
    padding: 8px 10px; border-bottom: 1px solid #E0E0E0; color: #000000; vertical-align: top;
    text-align: justify; font-size: 17px;
  }
  .pdf-cron-root table.itens tbody td.c { text-align: center; }
  .pdf-cron-root table.itens tbody tr.even td { background: #F5F5F5; }
  .pdf-cron-root table.itens tbody tr { page-break-inside: avoid; }

  /* Gantt — compacto para caber melhor com blocos de etapas na mesma página */
  .pdf-cron-root .gantt-wrap {
    margin: 0 4px 10px; padding: 10px; background: #FAFAFA; border: 1.5px solid #BDBDBD;
    border-radius: 6px;
  }
  .pdf-cron-root .gantt-axis {
    display: flex; align-items: center; gap: 10px; margin-bottom: 8px; padding-bottom: 6px;
    border-bottom: 2px solid #616161;
  }
  .pdf-cron-root .gantt-axis .axis-label { width: 300px; flex-shrink: 0; }
  .pdf-cron-root .gantt-axis .axis-dur { width: 55px; flex-shrink: 0; }
  .pdf-cron-root .gantt-axis .axis-ticks {
    flex: 1; position: relative; height: 24px;
  }
  .pdf-cron-root .gantt-axis .tick {
    position: absolute; top: 0; transform: translateX(-50%); display: flex;
    flex-direction: column; align-items: center;
  }
  .pdf-cron-root .gantt-axis .tick-mark { width: 2px; height: 6px; background: #212121; }
  .pdf-cron-root .gantt-axis .tick-label {
    font-size: 11px; color: #212121; font-weight: 700; font-family: Consolas, 'Courier New', ui-monospace, monospace;
    margin-top: 2px; white-space: nowrap;
  }
  .pdf-cron-root .gantt-row {
    display: flex; align-items: center; gap: 10px; font-size: 12px; margin-bottom: 4px;
    min-height: 30px;
  }
  .pdf-cron-root .gantt-row .nome {
    width: 300px; color: #000000; font-weight: 700; font-size: 12px;
    white-space: normal; word-wrap: break-word; overflow-wrap: break-word;
    line-height: 1.2; padding-right: 4px; flex-shrink: 0;
    font-family: Arial, 'Helvetica Neue', sans-serif;
  }
  .pdf-cron-root .gantt-row .track {
    flex: 1; position: relative; height: 22px; background: #E0E0E0; border-radius: 4px; overflow: hidden;
  }
  .pdf-cron-root .gantt-row .vgrid {
    position: absolute; top: 0; bottom: 0; width: 1.5px; background: rgba(97, 97, 97, 0.55);
    pointer-events: none; z-index: 1;
  }
  .pdf-cron-root .gantt-row .bar {
    position: absolute; height: 100%; border-radius: 3px; overflow: hidden;
    display: flex; align-items: center; justify-content: center; z-index: 2;
    box-shadow: inset 0 -2px 0 rgba(0,0,0,0.15);
  }
  .pdf-cron-root .gantt-row .bar-fill {
    position: absolute; left: 0; top: 0; bottom: 0;
    background: rgba(255, 255, 255, 0.4);
  }
  .pdf-cron-root .gantt-row .bar-label {
    position: relative; z-index: 3; color: #fff; font-size: 11px; font-weight: 800;
    text-shadow: 0 1px 2px rgba(0,0,0,0.55); font-family: Arial, sans-serif;
  }
  .pdf-cron-root .gantt-row .bar.b-pendente { background: #D32F2F; }
  .pdf-cron-root .gantt-row .bar.b-em_andamento { background: #2196F3; }
  .pdf-cron-root .gantt-row .bar.b-concluido { background: #4CAF50; }
  .pdf-cron-root .gantt-row .bar.b-atrasado { background: #FF9800; }
  .pdf-cron-root .gantt-row .bar.crit { box-shadow: inset 0 -2px 0 rgba(0,0,0,0.15), 0 0 0 2.5px #9C27B0; }
  .pdf-cron-root .gantt-row .dur { width: 55px; text-align: right; font-family: Consolas, 'Courier New', monospace; color: #000000; font-size: 12px; font-weight: 700; flex-shrink: 0; }
  .pdf-cron-root .gantt-legend { display: flex; gap: 14px; font-size: 11px; color: #000000; margin-top: 8px; padding-top: 6px; border-top: 1.5px solid #BDBDBD; font-weight: 700; }
  .pdf-cron-root .gantt-legend span { display: flex; align-items: center; gap: 6px; }
  .pdf-cron-root .gantt-legend .dot { width: 12px; height: 12px; border-radius: 3px; }

  .pdf-cron-root .imprev-row {
    border-left: 5px solid #FF9800; background: #FFF8E1; padding: 12px 14px; margin: 0 4px 10px;
    border-radius: 0 4px 4px 0; font-size: 12px; page-break-inside: avoid;
    color: #000000; line-height: 1.45; text-align: justify;
  }
  .pdf-cron-root .imprev-row .top { display: flex; justify-content: space-between; margin-bottom: 6px; font-weight: 700; }
  .pdf-cron-root .imprev-row .top b { color: #E65100; font-family: 'Arial Black', Arial, sans-serif; font-size: 13px; }
  .pdf-cron-root .imprev-row .perdidos { color: #D32F2F; font-weight: 800; font-family: 'Arial Black', Arial, sans-serif; }

  .pdf-cron-root .plano-block {
    border: 2.5px solid #D4A437; border-radius: 6px; padding: 16px; margin: 0 4px 18px;
    background: #FFFDE7; page-break-inside: avoid;
    font-size: 12px; color: #000000; text-align: justify; line-height: 1.4;
  }
  .pdf-cron-root .plano-block h3 { color: #6D4C00; margin-top: 0; font-size: 15px; }
  .pdf-cron-root .plano-block p { text-align: justify; margin: 6px 0; }
  .pdf-cron-root .plano-block ul { padding-left: 22px; font-size: 12px; margin: 8px 0; line-height: 1.55; color: #000000; text-align: justify; }

  .pdf-cron-root .footer {
    margin: 22px 4px 0; padding-top: 12px; border-top: 2px solid #BDBDBD;
    display: flex; justify-content: space-between; font-size: 11px; color: #212121; font-weight: 600;
  }
  .pdf-cron-root .footer b { color: #1A237E; }

  .pdf-cron-root .page-break { page-break-before: always; height: 0; }
</style>
`;

function buildEtapaBlock(etapa, etapas) {
  const dep = etapa.etapa_dependente_id ? etapas.find(e => e.id === etapa.etapa_dependente_id) : null;
  const itensRows = (etapa.itens || []).map((it, idx) => `
    <tr class="${idx % 2 === 0 ? 'even' : ''}">
      <td>${safeText(it.nome_servico)}</td>
      <td>${statusBadge(it.status)}</td>
      <td style="color:#616161">${safeText(it.observacao) || "—"}</td>
    </tr>`).join("");

  return `
    <div class="etapa-block ${etapa.critico ? 'critical' : ''}">
      <div class="etapa-header">
        <div class="titulo-row">
          <span class="ordem">#${etapa.ordem}</span>
          <span class="nome">${safeText(etapa.titulo)}</span>
          ${etapa.critico ? '<span style="font-size:11px;padding:3px 8px;background:#F3E5F5;color:#6A1B9A;border-radius:4px;font-weight:800;font-family:\'Arial Black\',Arial,sans-serif">CRÍTICA</span>' : ''}
          ${statusBadge(etapa.status)}
          <span class="duracao">${etapa.duracao_estimada_dias}d</span>
        </div>
        <table class="meta">
          <tbody>
            <tr>
              <td style="width:33%"><b>Início:</b> ${fmtDate(etapa.data_inicio_calculada)}</td>
              <td style="width:33%"><b>Fim:</b> ${fmtDate(etapa.data_fim_calculada)}</td>
              <td style="width:34%"><b>Folga:</b> ${etapa.folga ?? 0}d</td>
            </tr>
            <tr>
              <td colspan="3"><b>Dependência:</b> ${dep ? safeText(dep.titulo) : "Nenhuma"}</td>
            </tr>
          </tbody>
        </table>
      </div>
      ${etapa.escopo ? `
        <div class="escopo-banner">▸ Escopo · Etapa #${etapa.ordem} — ${safeText(etapa.titulo)}</div>
        <div class="escopo">${safeText(etapa.escopo)}</div>
      ` : ''}
      ${etapa.itens && etapa.itens.length > 0 ? `
        <table class="itens">
          <thead>
            <tr><th colspan="3" class="continuacao">↳ Continuação: Etapa #${etapa.ordem} — ${safeText(etapa.titulo)}</th></tr>
            <tr><th>Item de Serviço</th><th style="width:130px">Status</th><th>Observação</th></tr>
          </thead>
          <tbody>${itensRows}</tbody>
        </table>
      ` : '<p style="font-size:11px;color:#616161;margin-top:8px;font-style:italic">Nenhum item de serviço cadastrado.</p>'}
    </div>
  `;
}

function buildGanttHtml(etapas, dataInicio) {
  if (!etapas || etapas.length === 0) return "";
  const maxDay = Math.max(...etapas.map(e => e.ef || 0), 1);
  const dayWidth = 100 / maxDay;

  // Build date axis ticks (one tick every ~30 days or proportional, max 12 ticks)
  const startDate = dataInicio ? new Date(dataInicio + "T00:00:00") : new Date();
  const tickStep = Math.max(Math.ceil(maxDay / 10), 1);
  const ticks = [];
  for (let d = 0; d <= maxDay; d += tickStep) {
    const dt = new Date(startDate);
    dt.setDate(dt.getDate() + d);
    const dia = String(dt.getDate()).padStart(2, "0");
    const mes = String(dt.getMonth() + 1).padStart(2, "0");
    ticks.push({ day: d, label: `${dia}/${mes}`, leftPct: (d * dayWidth) });
  }
  // Ensure last tick is the final day
  if (ticks[ticks.length - 1].day !== maxDay) {
    const dt = new Date(startDate);
    dt.setDate(dt.getDate() + maxDay);
    const dia = String(dt.getDate()).padStart(2, "0");
    const mes = String(dt.getMonth() + 1).padStart(2, "0");
    ticks.push({ day: maxDay, label: `${dia}/${mes}`, leftPct: 100 });
  }

  const dateAxisHtml = `
    <div class="gantt-axis">
      <div class="axis-label"></div>
      <div class="axis-ticks">
        ${ticks.map(t => `
          <div class="tick" style="left:${t.leftPct}%">
            <div class="tick-mark"></div>
            <div class="tick-label">${t.label}</div>
          </div>`).join("")}
      </div>
      <div class="axis-dur"></div>
    </div>
  `;

  const gridLinesHtml = ticks.map(t => `<div class="vgrid" style="left:${t.leftPct}%"></div>`).join("");

  const rows = etapas.map(e => {
    const left = (e.es || 0) * dayWidth;
    const width = Math.max((e.duracao_estimada_dias || 1) * dayWidth, 1);
    const cls = `b-${e.status || 'pendente'}`;
    const pctItens = e.total_itens > 0 ? Math.round((e.itens_concluidos / e.total_itens) * 100) : 0;
    const pctProgresso = e.status === "concluido" ? 100 : pctItens;
    return `
      <div class="gantt-row">
        <div class="nome">${safeText(e.titulo)}</div>
        <div class="track">
          ${gridLinesHtml}
          <div class="bar ${cls} ${e.critico ? 'crit' : ''}" style="left:${left}%;width:${width}%">
            ${pctProgresso > 0 ? `<div class="bar-fill" style="width:${pctProgresso}%"></div>` : ''}
            ${width > 6 ? `<span class="bar-label">${pctProgresso}%</span>` : ''}
          </div>
        </div>
        <div class="dur">${e.duracao_estimada_dias}d</div>
      </div>`;
  }).join("");

  return `
    <h2>Linha do Tempo (Gantt)</h2>
    <div class="gantt-wrap">
      ${dateAxisHtml}
      ${rows}
      <div class="gantt-legend">
        <span><span class="dot" style="background:#D32F2F"></span> Pendente</span>
        <span><span class="dot" style="background:#2196F3"></span> Em Andamento</span>
        <span><span class="dot" style="background:#4CAF50"></span> Concluído</span>
        <span><span class="dot" style="background:#FF9800"></span> Atrasado</span>
        <span><span class="dot" style="border:2px solid #9C27B0;background:#fff"></span> Caminho Crítico</span>
        <span style="margin-left:auto;font-family:Consolas,monospace;font-weight:700">${maxDay} dias</span>
      </div>
    </div>
  `;
}

/**
 * Pre-flight pagination optimization using invisible spacers.
 * Measures the real rendered position/height of atomic blocks. If a block
 * would cross a page boundary, inserts a transparent spacer div BEFORE the
 * block to push it to the start of the next page. Works with rasterized
 * canvas (html2canvas) because the spacer occupies real vertical space.
 */
function optimizePagination(rootEl) {
  const pageHeightPx = 1517; // A4 usable canvas height at width 1080px with 15mm top/bottom margins
  const safetyMarginPx = 6;

  const blocks = Array.from(
    rootEl.querySelectorAll(".etapa-block, .plano-block, .imprev-row")
  );
  if (!blocks.length) return;

  const rootTop = rootEl.getBoundingClientRect().top + window.scrollY;

  blocks.forEach((block) => {
    // Re-measure each iteration because previous spacers may have shifted layout
    const rect = block.getBoundingClientRect();
    const top = rect.top + window.scrollY - rootTop;
    const height = rect.height;
    if (height <= 0 || height >= pageHeightPx) return;

    const currentPageIndex = Math.floor(top / pageHeightPx);
    const currentPageEnd = (currentPageIndex + 1) * pageHeightPx - safetyMarginPx;
    const bottom = top + height;

    if (bottom > currentPageEnd) {
      // Insert invisible spacer to push this block to next page
      const gap = currentPageEnd + safetyMarginPx - top;
      if (gap > 0) {
        const spacer = document.createElement("div");
        spacer.className = "pdf-pagination-spacer";
        spacer.style.height = `${gap}px`;
        spacer.style.width = "100%";
        spacer.style.background = "transparent";
        spacer.setAttribute("aria-hidden", "true");
        block.parentNode.insertBefore(spacer, block);
      }
    }
  });
}

async function renderAndDownload(htmlInner, filename) {
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.left = "-10000px";
  container.style.top = "0";
  container.style.background = "#fff";
  container.innerHTML = htmlInner;
  document.body.appendChild(container);

  try {
    const root = container.querySelector(".pdf-cron-root");
    // Wait for fonts + all images (logo, QR code) to fully load so measured
    // positions are accurate before running pagination optimization.
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch { /* no-op */ }
    }
    const imgs = Array.from(root.querySelectorAll("img"));
    await Promise.all(imgs.map(img => {
      if (img.complete && img.naturalHeight > 0) return Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });
    }));
    // Extra frame for layout to fully settle after images
    await new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 80)));

    optimizePagination(root);

    const worker = html2pdf()
      .from(root)
      .set({
        margin: PDF_MARGINS,
        filename,
        image: { type: "jpeg", quality: 1 },
        html2canvas: {
          scale: 4,
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
          width: 1080,
          windowWidth: 1080,
          scrollX: 0,
          scrollY: 0,
          letterRendering: true,
          logging: false,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait", compress: true, putOnlyUsedFonts: true },
        pagebreak: {
          mode: ["css"],
          avoid: [".plano-block", ".imprev-row", ".card", "tr", "h1", "h2", "h3", ".etapa-header", ".titulo-row", ".meta"],
        },
      });
    const blob = await worker.outputPdf("blob");
    return { blob, filename };
  } finally {
    document.body.removeChild(container);
  }
}

/** Generate PDF for a single etapa */
export async function gerarPDFEtapa({ etapa, etapas, contrato, cronograma }) {
  const dataEmissao = new Date().toLocaleString("pt-BR");
  const filename = `Cronograma_Etapa_${sanitize(etapa.titulo)}_${sanitize(contrato?.titulo || contrato?.numero || "contrato")}.pdf`;
  const qrDataUrl = await buildCronogramaQR(cronograma?.id);

  const htmlInner = `
    ${baseStyles}
    <div class="pdf-cron-root">
      ${headerHtml(`Cronograma · Etapa "${fixLegacyText(etapa.titulo)}"`, contrato, cronograma, dataEmissao, qrDataUrl)}
      <h1>Detalhe da Etapa: <span style="color:#D4A437">${safeText(etapa.titulo)}</span></h1>
      <p style="margin:0 4px 14px;font-size:12px;color:#424242;text-align:justify;line-height:1.4">Relatório individual da etapa com escopo, datas calculadas (CPM) e itens de serviço.</p>
      ${buildEtapaBlock(etapa, etapas)}
      <div class="footer">
        <div><b>${BRAND.fullName}</b> · Cronograma de Obra</div>
        <div>${dataEmissao}</div>
      </div>
    </div>
  `;
  return await renderAndDownload(htmlInner, filename);
}

/** Generate complete PDF for cronograma */
export async function gerarPDFCompleto({ cronograma, contrato, etapas, imprevistos, indicadores, planoAcao }) {
  const dataEmissao = new Date().toLocaleString("pt-BR");
  const dataHoje = new Date().toISOString().split("T")[0];
  const filename = `Cronograma_Completo_${sanitize(contrato?.titulo || contrato?.numero || "contrato")}_${dataHoje}.pdf`;
  const qrDataUrl = await buildCronogramaQR(cronograma?.id);

  const indicadoresHtml = `
    <h2>Indicadores</h2>
    <div class="indicadores">
      <div class="card">
        <div class="lbl">Prazo Previsto</div>
        <div class="val">${cronograma.duracao_prevista_dias}<span style="font-size:12px;color:#616161;font-weight:600"> dias</span></div>
        <div style="font-size:11px;color:#424242;margin-top:3px">${fmtDate(cronograma.data_inicio)} → ${fmtDate(cronograma.data_conclusao_prevista)}</div>
      </div>
      <div class="card ${indicadores.pct_progresso_fisico >= 50 ? 'ok' : ''}">
        <div class="lbl">Progresso Físico</div>
        <div class="val">${indicadores.pct_progresso_fisico}%</div>
        <div style="font-size:11px;color:#424242;margin-top:3px">${indicadores.itens_concluidos}/${indicadores.total_itens} itens</div>
      </div>
      <div class="card ${indicadores.dias_perdidos_total > 0 ? 'danger' : ''}">
        <div class="lbl">Dias Perdidos</div>
        <div class="val">${indicadores.dias_perdidos_total}</div>
        <div style="font-size:11px;color:#424242;margin-top:3px">Desvio: ${indicadores.desvio_pct}%</div>
      </div>
      <div class="card">
        <div class="lbl">Conclusão Projetada</div>
        <div class="val" style="font-size:15px">${fmtDate(indicadores.data_conclusao_projetada)}</div>
        <div style="font-size:11px;color:#424242;margin-top:3px">Etapas total: ${indicadores.duracao_etapas_total}d</div>
      </div>
    </div>
    ${indicadores.alerta_desvio ? `
      <div style="margin:0 4px 14px;padding:12px 14px;background:#FFEBEE;border-left:5px solid #D32F2F;border-radius:4px;font-size:12px;color:#B71C1C;font-weight:600;line-height:1.4;text-align:justify">
        <b style="font-family:'Arial Black',Arial,sans-serif">⚠ ATENÇÃO:</b> O desvio acumulado de ${indicadores.desvio_pct}% ultrapassou o limite de 10% do prazo previsto.
        ${indicadores.caminho_critico_atrasado ? ' <b>Caminho crítico está atrasado.</b>' : ''}
      </div>
    ` : ''}
  `;

  const etapasHtml = `
    <h2>Etapas da Obra (${etapas.length})</h2>
    ${etapas.map(e => buildEtapaBlock(e, etapas)).join("")}
  `;

  const ganttHtml = buildGanttHtml(etapas, cronograma.data_inicio);

  const imprevistosHtml = imprevistos.length === 0 ? "" : `
    <h2>Registro de Imprevistos (${imprevistos.length})</h2>
    ${imprevistos.map(i => {
      const etapa = i.etapa_id ? etapas.find(e => e.id === i.etapa_id) : null;
      return `
        <div class="imprev-row">
          <div class="top">
            <b>${escapeHtml(TIPO_LABEL[i.tipo] || i.tipo)}</b>
            <span class="perdidos">-${i.dias_perdidos} dia${i.dias_perdidos === 1 ? '' : 's'}</span>
          </div>
          <div><b>Data:</b> ${fmtDate(i.data_ocorrencia)} ${etapa ? `· <b>Etapa:</b> ${safeText(etapa.titulo)}` : ''}</div>
          ${i.descricao ? `<div style="margin-top:5px;color:#E65100">${safeText(i.descricao)}</div>` : ''}
        </div>`;
    }).join("")}
  `;

  const planoHtml = !planoAcao ? "" : `
    <h2>Plano de Ação · Replanejamento</h2>
    <div class="plano-block">
      <h3>Resumo do Desvio</h3>
      <p style="font-size:12px;color:#000000;margin:6px 0;line-height:1.45;text-align:justify">${safeText(planoAcao.resumo_desvio)}</p>
      <h3>Foco da Próxima Etapa</h3>
      <p style="font-size:12px;color:#000000;margin:6px 0;line-height:1.45;text-align:justify">${safeText(planoAcao.focus_proxima_etapa)}</p>
      <h3>Recomendações</h3>
      <ul>
        ${(planoAcao.recomendacoes || []).map(r => `<li>${safeText(r)}</li>`).join("")}
      </ul>
    </div>
  `;

  const htmlInner = `
    ${baseStyles}
    <div class="pdf-cron-root">
      ${headerHtml("Cronograma Completo de Obra", contrato, cronograma, dataEmissao, qrDataUrl)}
      <h1>Cronograma <span style="color:#D4A437">Completo</span></h1>
      <p style="margin:0 4px 18px;font-size:12px;color:#424242;line-height:1.45;text-align:justify">Documento consolidado com etapas, gráfico Gantt, indicadores, imprevistos e plano de ação (quando aplicável).</p>

      ${indicadoresHtml}
      ${ganttHtml}
      ${etapasHtml}
      ${imprevistosHtml}
      ${planoHtml}

      <div class="footer">
        <div><b>${BRAND.fullName}</b> · Cronograma de Obra · Status: ${cronograma.status === "finalizado" ? "Finalizado" : "Rascunho"}</div>
        <div>${dataEmissao}</div>
      </div>
    </div>
  `;
  return await renderAndDownload(htmlInner, filename);
}
